import { Sun, Wind, Zap, Radio } from "lucide-react";

function Row({ icon: Icon, label, value, unit, tone = "neutral", testid }) {
  const color =
    tone === "warn"
      ? "var(--accent-warning)"
      : tone === "hot"
      ? "var(--rose-gold)"
      : tone === "teal"
      ? "var(--parchment)"
      : "var(--parchment-soft)";
  return (
    <div
      className="flex items-center justify-between py-4 border-b border-[color:var(--gold)]/10 last:border-0"
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <Icon size={16} className="text-[color:var(--gold)]/70" />
        <span className="label-overline">{label}</span>
      </div>
      <div className="font-serif-display text-2xl tracking-tight" style={{ color }}>
        {value}
        {unit && <span className="text-[color:var(--text-secondary)] text-xs font-sans-body ml-1">{unit}</span>}
      </div>
    </div>
  );
}

export default function CosmicPanel({ current }) {
  if (!current) {
    return (
      <div className="parchment-card rounded-2xl p-8 h-full">
        <div className="label-overline">Cosmic Telemetry</div>
        <div className="text-[color:var(--text-muted)] mt-3">Reading the sky…</div>
      </div>
    );
  }
  const kpTone = current.kp_index >= 5 ? "warn" : current.kp_index >= 4 ? "hot" : "neutral";
  const flareTone = current.xray_flux_class.startsWith("X")
    ? "warn"
    : current.xray_flux_class.startsWith("M")
    ? "hot"
    : "neutral";

  return (
    <div className="parchment-card rounded-2xl p-8 md:p-10 h-full hover-lift" data-testid="cosmic-panel">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="label-overline constellation-underline">Cosmic Telemetry</div>
          <h2 className="mt-3 font-serif-display text-white text-2xl md:text-3xl tracking-tight">
            Sun, wind &amp; field
          </h2>
          <p className="font-serif-italic text-[color:var(--text-secondary)] mt-1">what the sky is doing</p>
        </div>
        <div
          className="px-3 py-1 rounded-full border font-serif-italic text-sm capitalize"
          style={{
            borderColor:
              current.geomag_status === "storm"
                ? "var(--accent-warning)"
                : current.geomag_status === "elevated"
                ? "var(--rose-gold)"
                : "var(--gold)",
            color:
              current.geomag_status === "storm"
                ? "var(--accent-warning)"
                : current.geomag_status === "elevated"
                ? "var(--rose-gold)"
                : "var(--gold)",
          }}
        >
          {current.geomag_status}
        </div>
      </div>

      <div>
        <Row icon={Radio} label="Kp Index" value={current.kp_index.toFixed(2)} unit={`· ${current.kp_status}`} tone={kpTone} testid="row-kp" />
        <Row icon={Wind} label="Solar Wind" value={current.solar_wind_speed?.toFixed(0) ?? "—"} unit="km/s" testid="row-wind-speed" />
        <Row icon={Wind} label="IMF Bt" value={current.solar_wind_density?.toFixed(1) ?? "—"} unit="nT" testid="row-wind-density" />
        <Row icon={Sun} label="X-Ray Class" value={current.xray_flux_class} tone={flareTone} testid="row-xray" />
        <Row
          icon={Zap}
          label="X-Ray Flux"
          value={current.xray_flux_wm2 ? current.xray_flux_wm2.toExponential(2) : "—"}
          unit="W/m²"
          testid="row-xray-flux"
        />
      </div>
    </div>
  );
}
